import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import "./chatReactions.scss";

const GAP = 6;
const EDGE = 8;

const HIDDEN_STYLE = { visibility: "hidden" };

const getScrollParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }
  return null;
};

// Union of the avatar box and everything drawn around it (frame, badges),
// since decorative frames overflow the avatar element itself.
const getAvatarRect = (anchor) => {
  const avatar = anchor
    .closest(".chat-message-row")
    ?.querySelector(".chat-framed-avatar");
  if (!avatar) return null;

  const rects = [avatar, ...avatar.querySelectorAll("*")]
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width && r.height);
  if (!rects.length) return null;

  return {
    left: Math.min(...rects.map((r) => r.left)),
    right: Math.max(...rects.map((r) => r.right)),
    top: Math.min(...rects.map((r) => r.top)),
    bottom: Math.max(...rects.map((r) => r.bottom)),
  };
};

const computePosition = (anchor, picker) => {
  const anchorRect = anchor.getBoundingClientRect();
  const width = picker.offsetWidth;
  const height = picker.offsetHeight;

  const viewport = {
    left: EDGE,
    right: window.innerWidth - EDGE,
    top: EDGE,
    bottom: window.innerHeight - EDGE,
  };
  const scrollRect = getScrollParent(anchor)?.getBoundingClientRect();
  const bounds = scrollRect
    ? {
        left: Math.max(viewport.left, scrollRect.left + EDGE),
        right: Math.min(viewport.right, scrollRect.right - EDGE),
        top: Math.max(viewport.top, scrollRect.top),
        bottom: Math.min(viewport.bottom, scrollRect.bottom),
      }
    : viewport;

  // Message scrolled out of the chat container: keep the picker out of sight.
  if (anchorRect.bottom < bounds.top || anchorRect.top > bounds.bottom) {
    return null;
  }

  const avatar = getAvatarRect(anchor);
  const anchorCenter = (anchorRect.left + anchorRect.right) / 2;
  const avatarOnLeft = avatar
    ? (avatar.left + avatar.right) / 2 < anchorCenter
    : anchorCenter < (bounds.left + bounds.right) / 2;

  // Horizontal: start at the bubble edge next to the avatar and grow away
  // from it, staying clear of the avatar column when there is room.
  let minLeft = bounds.left;
  let maxRight = bounds.right;
  let clearsAvatarSideways = !avatar;
  if (avatar) {
    const avatarEdgeMin = avatarOnLeft ? avatar.right + GAP : minLeft;
    const avatarEdgeMax = avatarOnLeft ? maxRight : avatar.left - GAP;
    if (avatarEdgeMax - avatarEdgeMin >= width) {
      minLeft = avatarEdgeMin;
      maxRight = avatarEdgeMax;
      clearsAvatarSideways = true;
    }
  }

  let left = avatarOnLeft ? anchorRect.left : anchorRect.right - width;
  left = Math.max(minLeft, Math.min(left, maxRight - width));
  left = Math.max(viewport.left, Math.min(left, viewport.right - width));

  // Vertical: above the bubble, flipped below when there is no room.
  // If the picker could not clear the avatar sideways, clear it vertically.
  let refTop = anchorRect.top;
  let refBottom = anchorRect.bottom;
  if (!clearsAvatarSideways && avatar) {
    refTop = Math.min(refTop, avatar.top);
    refBottom = Math.max(refBottom, avatar.bottom);
  }

  const above = refTop - GAP - height;
  const below = refBottom + GAP;
  let top;
  if (above >= bounds.top) top = above;
  else if (below + height <= bounds.bottom) top = below;
  else if (above >= viewport.top) top = above;
  else if (below + height <= viewport.bottom) top = below;
  else top = viewport.top;

  return { top, left };
};

const ChatReactionPicker = ({
  emojis = [],
  selected = null,
  onPick,
  anchorRef,
}) => {
  const pickerRef = useRef(null);
  const [style, setStyle] = useState(HIDDEN_STYLE);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    const picker = pickerRef.current;
    if (!anchor || !picker) return;
    const pos = computePosition(anchor, picker);
    setStyle(pos ? { top: pos.top, left: pos.left } : HIDDEN_STYLE);
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!emojis.length) return undefined;
    updatePosition();

    let frame = null;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updatePosition();
      });
    };

    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, true);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, true);
    };
  }, [emojis.length, updatePosition]);

  if (!emojis.length) return null;

  return createPortal(
    <div
      ref={pickerRef}
      className="chat-reaction-picker"
      role="listbox"
      style={style}
    >
      {emojis.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className={`chat-reaction-picker__emoji${
            selected === emoji ? " is-selected" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onPick?.(emoji);
          }}
        >
          {emoji}
        </button>
      ))}
    </div>,
    document.body
  );
};

ChatReactionPicker.propTypes = {
  emojis: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.string,
  onPick: PropTypes.func,
  anchorRef: PropTypes.shape({ current: PropTypes.any }),
};

export default ChatReactionPicker;
